/**
 * Shared types for the edit-payment-method dialog.
 *
 * Extracted to a leaf file to break a circular dependency between
 * `components/management/edit-payment-method-dialog.tsx` and
 * `components/checkout/hooks/useAutocomplete.tsx` (which needs to
 * constrain its generic `T` against these form types).
 */
import { z } from "zod";
import {
  type AddressField,
  dynamicAddressSchema,
} from "@/networking/addresses.api";

export const createEditPaymentMethodFormSchema = (
  addressFields: AddressField[],
) => {
  return z.object({
    billing_address: dynamicAddressSchema(addressFields, {
      nameRequired: "Name is required",
      addressLine1Required: "Address line 1 is required",
      cityRequired: "City is required",
      stateRequired: "State is required",
      postalCodeRequired: "Postal code is required",
      countryRequired: "Country is required",
      phoneRequired: "Enter a phone number for this address",
    }).extend({
      country_code: z.string().min(1, "Country is required"),
    }),
    set_as_default: z.boolean(),
  });
};

export type InternalFormData = z.infer<
  ReturnType<typeof createEditPaymentMethodFormSchema>
>;

export type EditPaymentMethodFormData = {
  billing_address: {
    name: string;
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip: string;
    country_code: string;
    phone?: string;
  };
  set_as_default: boolean;
};
